import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import {
    AlertTriangle, CheckCircle2, Clock, FolderOpen, Loader2, Mail, Plus, RefreshCw, Send, Trash2, X, XCircle,
} from 'lucide-react';
import { ablageHinweis, pruefeVersandEmpfaenger, type VersandEmpfaenger, type VersandErgebnis } from './planungVersand';
import type { Planungsfrage } from './types';
import { datumDeutsch } from './unterlagenLogic';

/** Was der Dialog zum Stempel sagt: `text` bei Erfolg, `fehler` sonst —
 *  „Stempel nachholen" nur bei `nachholbar` (Mail raus, allein der Stempel fehlt). */
export interface StempelAnzeige {
    text: string | null;
    fehler: string | null;
    nachholbar: boolean;
}

interface Props {
    titel: string;
    untertitel?: string;
    /** Aus der Empfaenger-Zuordnung; kommt asynchron, solange `empfaengerLaden`. */
    empfaengerVorgabe: VersandEmpfaenger[];
    empfaengerLaden: boolean;
    /** Fehlt ein Empfaenger oder seine E-Mail: der Satz dazu. */
    empfaengerHinweis: string | null;
    betreffVorgabe: string;
    textVorgabe: string;
    /** Die Fragen auf dem Blatt — zur Kontrolle vor dem Klick. */
    fragen?: ReadonlyArray<Pick<Planungsfrage, 'id' | 'code' | 'frage' | 'kritikalitaet' | 'gestellt_am'>>;
    /** Was der Versand ausloest (Stempel, Ablage) — EIN Satz unter dem Formular. */
    folgenHinweis?: string;
    laeuft: boolean;
    isReadOnly: boolean;
    /** Fehler VOR dem Versand (PDF nicht erzeugbar, Nur-Ansicht). */
    fehler: string | null;
    /** Nach dem Versand: der Dialog zeigt das Ergebnis statt des Formulars. */
    ergebnis: VersandErgebnis | null;
    stempel: StempelAnzeige | null;
    onVersenden: (empfaenger: VersandEmpfaenger[], betreff: string, text: string) => void;
    onStempelNachholen: () => void;
    /** Zurueck ins Formular nach einem (Teil-)Fehlschlag. */
    onErneut: () => void;
    onSchliessen: () => void;
}

const KRIT_FARBE: Record<string, string> = {
    A: 'bg-rose-50 text-rose-700 border-rose-200',
    B: 'bg-amber-50 text-amber-700 border-amber-200',
    C: 'bg-slate-100 text-slate-500 border-slate-200',
};

const FELD = 'w-full px-3 py-2 border border-slate-200 rounded-lg text-sm disabled:bg-slate-50';

const istGleich = (a: string, b: string) => a.trim().toLowerCase() === b.trim().toLowerCase();

/** „17.10.2026, 10:03 Uhr" in Ortszeit; bei unbrauchbarem Wert der rohe String. */
const zeitpunktDeutsch = (iso: string): string => {
    const d = new Date(iso);
    if (!isFinite(d.getTime())) return iso;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${datumDeutsch(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`)}, ${hh}:${mm} Uhr`;
};

/**
 * Versand einer Unterlage der Planungsphase per E-Mail — Empfaenger,
 * Betreff, Text, das Blatt zur Kontrolle, danach das Ergebnis je Empfaenger
 * samt Ablage und Stempel.
 *
 * Reine Anzeige mit lokalem Formularzustand; versendet und gestempelt wird
 * im Modul (`planungVersand.ts`). Der Versand an Externe geschieht IMMER
 * durch diesen Klick eines Menschen (Entscheide E10/E11) — nichts hier
 * laeuft von selbst.
 *
 * Nach dem Versand bleibt der Dialog offen und zeigt, was geschah: jede
 * Adresse einzeln, die Ablage, der Stempel. Scheiterte der Stempel, steht
 * „Stempel nachholen" direkt daneben — die Mail ist dann RAUS, und das darf
 * nicht hinter einem „Schliessen" verschwinden.
 *
 * Per Portal an `document.body`, wie `HandbuchImportDialog`: der Modul-
 * inhalt scrollt in einem eigenen Container, und ein `fixed`-Dialog darin
 * haengt am naechsten Vorfahren mit `transform`.
 */
export const VersandDialog: React.FC<Props> = ({
    titel, untertitel, empfaengerVorgabe, empfaengerLaden, empfaengerHinweis, betreffVorgabe, textVorgabe,
    fragen, folgenHinweis, laeuft, isReadOnly, fehler, ergebnis, stempel,
    onVersenden, onStempelNachholen, onErneut, onSchliessen,
}) => {
    const [empfaenger, setEmpfaenger] = useState<VersandEmpfaenger[]>(empfaengerVorgabe);
    const [vorgabeUebernommen, setVorgabeUebernommen] = useState(!empfaengerLaden);
    const [betreff, setBetreff] = useState(betreffVorgabe);
    const [text, setText] = useState(textVorgabe);
    const [versucht, setVersucht] = useState(false);

    // Die Vorgabe kommt asynchron (die Zuordnung wird gelesen): EINMAL
    // uebernehmen, sobald sie da ist — im Rendern, nicht im Effekt (Muster
    // `useEmpfaengerZuordnung`). Danach gehoert die Liste dem Nutzer.
    if (!empfaengerLaden && !vorgabeUebernommen) {
        setEmpfaenger(empfaengerVorgabe);
        setVorgabeUebernommen(true);
    }

    const problem = pruefeVersandEmpfaenger(empfaenger);
    const betreffFehlt = !betreff.trim();
    const gesperrt = laeuft || isReadOnly || empfaengerLaden;

    const aendere = (i: number, feld: keyof VersandEmpfaenger, wert: string) =>
        setEmpfaenger(alt => alt.map((e, j) => (j === i ? { ...e, [feld]: wert } : e)));
    const entferne = (i: number) => setEmpfaenger(alt => alt.filter((_, j) => j !== i));
    const ergaenze = () => setEmpfaenger(alt => [...alt, { name: '', email: '' }]);

    const versenden = () => {
        setVersucht(true);
        if (gesperrt || problem || betreffFehlt) return;
        onVersenden(
            empfaenger.map(e => ({ name: e.name.trim(), email: e.email.trim() })),
            betreff.trim(),
            text,
        );
    };

    /** Zurueck ins Formular — bei Teilerfolg nur mit denen, die nichts bekamen. */
    const erneut = () => {
        if (ergebnis && ergebnis.versendetAn.length) {
            setEmpfaenger(alt => alt.filter(e => !ergebnis.versendetAn.some(v => istGleich(v, e.email))));
        }
        setVersucht(false);
        onErneut();
    };

    const anzahlFragen = fragen ? fragen.length : 0;
    const schonGestellt = fragen ? fragen.filter(f => f.gestellt_am).length : 0;

    return createPortal(
        <div className="fixed inset-0 z-[80] bg-slate-900/50 flex items-center justify-center p-4" role="dialog" aria-modal="true">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col min-h-0">

                <div className="flex items-start gap-3 px-5 py-4 border-b border-slate-200">
                    <Mail className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                    <div className="min-w-0 flex-1">
                        <h2 className="font-black text-slate-900">{titel}</h2>
                        {untertitel && <p className="text-xs text-slate-500">{untertitel}</p>}
                    </div>
                    <button type="button" onClick={onSchliessen} className="p-1 text-slate-400 hover:text-slate-600" aria-label="Schließen">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {!ergebnis ? (
                    <>
                        <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4 space-y-4">
                            {isReadOnly && (
                                <div className="bg-amber-50 border border-amber-200 rounded-xl px-3 py-2 text-xs text-amber-900">
                                    Nur-Ansicht: Es kann nichts versendet werden.
                                </div>
                            )}
                            {fehler && (
                                <div className="bg-rose-50 border border-rose-200 rounded-xl px-3 py-2 text-xs text-rose-900 flex items-start gap-2">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{fehler}</span>
                                </div>
                            )}

                            {/* ─── Empfaenger ──────────────────────────────── */}
                            <section>
                                <div className="flex items-center gap-2 mb-2">
                                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-wider">Empfänger</h3>
                                    {empfaengerLaden && (
                                        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
                                            <Loader2 className="w-3 h-3 animate-spin" /> Zuordnung wird gelesen …
                                        </span>
                                    )}
                                </div>
                                {empfaengerHinweis && !empfaengerLaden && (
                                    <div className="mb-2 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                        <span>{empfaengerHinweis}</span>
                                    </div>
                                )}
                                <div className="space-y-2">
                                    {empfaenger.map((e, i) => (
                                        <div key={i} className="flex flex-col sm:flex-row gap-2">
                                            <input
                                                type="text"
                                                value={e.name}
                                                disabled={gesperrt}
                                                onChange={ev => aendere(i, 'name', ev.target.value)}
                                                placeholder="Name"
                                                aria-label={`Name des Empfängers ${i + 1}`}
                                                className={`${FELD} flex-1 min-w-0`}
                                            />
                                            <input
                                                type="email"
                                                value={e.email}
                                                disabled={gesperrt}
                                                onChange={ev => aendere(i, 'email', ev.target.value)}
                                                placeholder="E-Mail (Pflicht)"
                                                aria-label={`E-Mail des Empfängers ${i + 1}`}
                                                className={`${FELD} flex-1 min-w-0`}
                                            />
                                            <button
                                                type="button"
                                                onClick={() => entferne(i)}
                                                disabled={gesperrt}
                                                className="p-2 text-slate-400 hover:text-rose-600 disabled:opacity-40 self-start"
                                                aria-label={`Empfänger ${i + 1} entfernen`}
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ))}
                                    <button
                                        type="button"
                                        onClick={ergaenze}
                                        disabled={gesperrt}
                                        className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 hover:text-blue-800 disabled:opacity-40"
                                    >
                                        <Plus className="w-3.5 h-3.5" /> Weiteren Empfänger
                                    </button>
                                </div>
                                {versucht && problem && (
                                    <p className="mt-2 text-xs text-rose-700 font-bold">{problem}</p>
                                )}
                            </section>

                            {/* ─── Betreff und Text ────────────────────────── */}
                            <section className="space-y-3">
                                <label className="block">
                                    <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Betreff</span>
                                    <input
                                        type="text"
                                        value={betreff}
                                        disabled={gesperrt}
                                        onChange={ev => setBetreff(ev.target.value)}
                                        className={FELD}
                                    />
                                    {versucht && betreffFehlt && (
                                        <span className="block mt-1 text-xs text-rose-700 font-bold">Ein Betreff ist nötig.</span>
                                    )}
                                </label>
                                <label className="block">
                                    <span className="block text-[11px] font-black text-slate-400 uppercase tracking-wider mb-1">Anschreiben</span>
                                    <textarea
                                        value={text}
                                        disabled={gesperrt}
                                        onChange={ev => setText(ev.target.value)}
                                        rows={8}
                                        className={`${FELD} leading-relaxed`}
                                    />
                                    <span className="block text-[11px] text-slate-400 mt-1">
                                        Das Blatt hängt als PDF an. Antworten des Empfängers landen beim Absender (Antwort-an).
                                    </span>
                                </label>
                            </section>

                            {/* ─── Die Fragen auf dem Blatt ────────────────── */}
                            {fragen && fragen.length > 0 && (
                                <section>
                                    <h3 className="text-[11px] font-black text-slate-400 uppercase tracking-wider mb-2">
                                        Auf dem Blatt: {anzahlFragen} {anzahlFragen === 1 ? 'Frage' : 'Fragen'}
                                        {schonGestellt > 0 && <> · {schonGestellt} davon bereits gestellt</>}
                                    </h3>
                                    <ul className="border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-48 overflow-y-auto">
                                        {fragen.map(f => (
                                            <li key={f.id} className="px-3 py-2 flex items-start gap-2 text-xs">
                                                <span className="font-mono font-bold text-slate-500 shrink-0">{f.code}</span>
                                                <span className={`shrink-0 text-[10px] font-black px-1.5 py-0.5 rounded border ${KRIT_FARBE[f.kritikalitaet] || KRIT_FARBE.C}`}>
                                                    {f.kritikalitaet}
                                                </span>
                                                <span className="flex-1 min-w-0 text-slate-700 line-clamp-2">{f.frage}</span>
                                                {f.gestellt_am && (
                                                    <span className="shrink-0 inline-flex items-center gap-1 text-[10px] text-slate-400" title="Bereits gestellt; das erste Stelldatum bleibt">
                                                        <Clock className="w-3 h-3" /> {datumDeutsch(f.gestellt_am) || 'gestellt'}
                                                    </span>
                                                )}
                                            </li>
                                        ))}
                                    </ul>
                                </section>
                            )}

                            {folgenHinweis && (
                                <p className="text-[11px] text-slate-500 leading-snug">{folgenHinweis}</p>
                            )}
                        </div>

                        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={onSchliessen}
                                disabled={laeuft}
                                className="px-3 py-2 text-xs font-bold text-slate-600 hover:text-slate-800 disabled:opacity-40"
                            >
                                Abbrechen
                            </button>
                            <button
                                type="button"
                                onClick={versenden}
                                disabled={gesperrt}
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-40"
                            >
                                {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
                                {laeuft ? 'Wird versendet …' : 'Versenden'}
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <div className="flex-1 overflow-y-auto min-h-0 px-5 py-4 space-y-4">
                            {/* ─── Ergebnis ────────────────────────────────── */}
                            <div className={`rounded-xl border px-4 py-3 text-sm flex items-start gap-2 ${
                                ergebnis.status === 'versendet' ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                                    : ergebnis.status === 'teilweise' ? 'bg-amber-50 border-amber-200 text-amber-900'
                                        : 'bg-rose-50 border-rose-200 text-rose-900'
                            }`}>
                                {ergebnis.status === 'fehler'
                                    ? <XCircle className="w-5 h-5 shrink-0 mt-0.5" />
                                    : ergebnis.status === 'teilweise'
                                        ? <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
                                        : <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />}
                                <div className="min-w-0 flex-1">
                                    <div className="font-bold">
                                        {ergebnis.status === 'versendet' && `Versendet an ${ergebnis.versendetAn.length} Empfänger.`}
                                        {ergebnis.status === 'teilweise' && `Nur teilweise versendet: ${ergebnis.versendetAn.length} erreicht, ${ergebnis.fehler.length} nicht.`}
                                        {ergebnis.status === 'fehler' && 'Es wurde nichts versendet.'}
                                    </div>
                                    {ergebnis.versendetAm && (
                                        <div className="text-xs opacity-80">
                                            Versandzeitpunkt {zeitpunktDeutsch(ergebnis.versendetAm)}
                                        </div>
                                    )}
                                </div>
                            </div>

                            <ul className="text-xs space-y-1">
                                {ergebnis.versendetAn.map(e => (
                                    <li key={e} className="flex items-center gap-2 text-emerald-800">
                                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0" /> {e}
                                    </li>
                                ))}
                                {ergebnis.fehler.map((f, i) => (
                                    <li key={`f${i}`} className="flex items-start gap-2 text-rose-800">
                                        <XCircle className="w-3.5 h-3.5 shrink-0 mt-0.5" /> <span className="break-words">{f}</span>
                                    </li>
                                ))}
                            </ul>
                            {ergebnis.fehler.length > 0 && ergebnis.status !== 'fehler' && (
                                <p className="text-[11px] text-slate-500 leading-snug">
                                    Ob eine fehlgeschlagene E-Mail dennoch hinausging, ist nicht in jedem Fall sicher. Im Zweifel
                                    beim Empfänger nachfragen, bevor erneut versendet wird.
                                </p>
                            )}

                            {/* ─── Ablage ──────────────────────────────────── */}
                            {ergebnis.ablage && (
                                <div className={`rounded-lg border px-3 py-2 text-xs flex items-start gap-2 ${
                                    ergebnis.ablage.status === 'fehler'
                                        ? 'bg-amber-50 border-amber-200 text-amber-900'
                                        : 'bg-slate-50 border-slate-200 text-slate-700'
                                }`}>
                                    <FolderOpen className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>
                                        {ergebnis.ablage.status === 'abgelegt' && <>In der Ablage gesichert: <strong>{ergebnis.ablage.dokument?.titel}</strong></>}
                                        {ergebnis.ablage.status === 'schon_abgelegt' && <>Die Fassung lag bereits in der Ablage: <strong>{ergebnis.ablage.dokument?.titel}</strong></>}
                                        {ergebnis.ablage.status === 'fehler' && <>
                                            <strong>Nicht in der Ablage gesichert.</strong> {ergebnis.ablage.fehler}
                                            {ergebnis.versendetAn.length > 0 && ' Die E-Mail ist trotzdem hinausgegangen.'}
                                        </>}
                                    </span>
                                </div>
                            )}
                            {/* Der Ablagesatz steht schon im Kasten darueber — hier nur der Rest. */}
                            {ergebnis.hinweise
                                .filter(hw => hw !== ablageHinweis(ergebnis.ablage))
                                .map((hw, i) => (
                                    <div key={`h${i}`} className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2">
                                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                        <span>{hw}</span>
                                    </div>
                                ))}

                            {/* ─── Stempel ─────────────────────────────────── */}
                            {stempel && stempel.text && (
                                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-900 flex items-start gap-2">
                                    <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span>{stempel.text}</span>
                                </div>
                            )}
                            {stempel && stempel.fehler && (
                                <div className="rounded-lg border border-rose-300 bg-rose-50 px-3 py-2 text-xs text-rose-900 flex items-start gap-2 flex-wrap">
                                    <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                                    <span className="flex-1 min-w-[12rem]">
                                        <strong>{stempel.nachholbar ? 'Stempel gescheitert.' : 'Nicht gestempelt.'}</strong> {stempel.fehler}
                                    </span>
                                    {stempel.nachholbar && (
                                        <button
                                            type="button"
                                            onClick={onStempelNachholen}
                                            disabled={laeuft || isReadOnly}
                                            className="inline-flex items-center gap-1.5 px-3 py-1.5 font-bold text-white bg-rose-700 rounded-lg hover:bg-rose-800 disabled:opacity-40"
                                        >
                                            {laeuft ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                                            Stempel nachholen
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="px-5 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
                            {ergebnis.status !== 'versendet' && (
                                <button
                                    type="button"
                                    onClick={erneut}
                                    disabled={laeuft || isReadOnly}
                                    className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-40"
                                >
                                    <RefreshCw className="w-3.5 h-3.5" />
                                    {ergebnis.status === 'teilweise' ? 'An die Übrigen erneut senden' : 'Erneut versuchen'}
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={onSchliessen}
                                disabled={laeuft}
                                className="px-4 py-2 text-xs font-bold text-white bg-slate-800 rounded-lg hover:bg-slate-900 disabled:opacity-40"
                            >
                                Schließen
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>,
        document.body,
    );
};
